import { Paragraph2, Title1 } from "@bounce/design-system"
import type { Nullable } from "@bounce/util"

import { HeroContainer } from "./HeroContainer"
import type { ImgixParams } from "../../../helpers/image"
import { Button } from "../../Inputs/Button/Button"
import { Link } from "../../Navigation/Link/Link"
import type { PNImageProps } from "../PNImage/PNImage"

export type HeroProps = {
  eyebrow?: Nullable<string>
  title?: Nullable<string>
  subtitle?: Nullable<string>
  src: PNImageProps["src"]
  imageSize?: "contain" | "cover"
  imgixParams?: ImgixParams
  ctaLabel?: Nullable<string>
  ctaHref?: Nullable<string>
  ctaOnClick?: (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  ctaSecondaryLabel?: Nullable<string>
  ctaSecondaryHref?: Nullable<string>
  ctaSecondaryOnClick?: (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  hideImageOnMobile?: boolean
}

export const Hero = ({
  ctaHref,
  ctaLabel,
  ctaOnClick,
  ctaSecondaryHref,
  ctaSecondaryLabel,
  ctaSecondaryOnClick,
  eyebrow,
  hideImageOnMobile = false,
  imageSize = "cover",
  imgixParams,
  src,
  subtitle,
  title,
}: HeroProps) => {
  const className = imageSize === "contain" ? "!min-h-fit" : undefined
  const imageClassName = imageSize === "contain" ? "!object-contain md:!w-1/3 lg:!w-1/2" : undefined
  const hasCta = !!ctaLabel || !!ctaSecondaryLabel

  return (
    <HeroContainer
      image={src}
      hideImageOnMobile={hideImageOnMobile}
      imgixParams={imgixParams}
      className={className}
      imageClassName={imageClassName}>
      {!!eyebrow && <Paragraph2 className="mb-2 flex text-neutral-800">{eyebrow}</Paragraph2>}
      {!!title && <Title1 className="mb-5 break-words text-black md:mb-6">{title}</Title1>}
      {!!subtitle && <p className="text-base text-black md:text-xl xl:text-2xl">{subtitle}</p>}
      {hasCta && (
        <div className="mt-10 flex w-full flex-col gap-6 md:flex-row">
          {!!ctaLabel && (
            <Button
              as={Link}
              href={ctaHref || "#"}
              onClick={ctaOnClick}
              color="coral"
              rel="nofollow"
              className="w-full md:w-fit">
              {ctaLabel}
            </Button>
          )}
          {!!ctaSecondaryLabel && (
            <Button
              as={Link}
              href={ctaSecondaryHref || "#"}
              onClick={ctaSecondaryOnClick}
              rel="nofollow"
              variant="ghost"
              className="w-full md:w-fit">
              {ctaSecondaryLabel}
            </Button>
          )}
        </div>
      )}
    </HeroContainer>
  )
}
